import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Download,
  Users,
  TicketCheck,
  DollarSign,
  Percent,
  PiggyBank,
  Loader2,
  Search,
  Receipt,
  CheckCircle2,
  RotateCcw,
  Star,
  BellRing,
  TrendingUp,
  ArrowRight,
  TicketPlus,
} from "lucide-react";
import { toast } from "sonner";
import { WaitlistNudgeBanner } from "../../components/WaitlistNudgeBanner";
import { DemandFunnelCard } from "../../components/DemandFunnelCard";
import {
  reportsApi,
  ordersCsvUrl,
  attendeesCsvUrl,
  attendeesXlsxUrl,
  centsToAUD,
  formatAuDate,
} from "../../lib/events";
import { api, extractError } from "../../lib/api";
import { RefundModal } from "../../components/RefundModal";
import ManualIssueDialog from "../../components/ManualIssueDialog";
import { useAuth } from "../../context/AuthContext";

const STATUS_TONE = {
  paid: "bg-emerald-100 text-emerald-900 border-emerald-500",
  complete: "bg-emerald-100 text-emerald-900 border-emerald-500",
  refunded: "bg-[var(--gt-accent)] text-white border-[var(--gt-accent)]",
  partially_refunded: "bg-amber-100 text-amber-900 border-amber-500",
  valid: "bg-emerald-100 text-emerald-900 border-emerald-500",
  checked_in: "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)]",
  voided: "bg-neutral-200 text-neutral-700 border-neutral-400",
  cancelled: "bg-neutral-200 text-neutral-700 border-neutral-400",
};

function Badge({ status, testid }) {
  const tone = STATUS_TONE[status] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${tone}`}
      data-testid={testid}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="gt-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <Icon size={18} className="text-[var(--gt-ink-2)]" />
      </div>
      <div className="gt-label mb-1">{label}</div>
      <div className="font-heading text-2xl sm:text-3xl font-black tracking-tighter leading-none" data-testid={`${testid}-value`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-[var(--gt-ink-3)]" data-testid={`${testid}-sub`}>{sub}</div>}
    </div>
  );
}

export default function ReportsPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [feedback, setFeedback] = useState({ count: 0, average: null, items: [] });
  const [waitlist, setWaitlist] = useState({ total: 0, active: 0, by_status: {}, active_by_section: {} });
  const [searchParams, setSearchParams] = useSearchParams();
  const _allowedTabs = ["orders", "attendees", "feedback", "waitlist"];
  const _initialTab = _allowedTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "orders";
  const [tab, _setTabState] = useState(_initialTab);
  const setTab = (t) => {
    _setTabState(t);
    setSearchParams(t === "orders" ? {} : { tab: t }, { replace: true });
  };
  const [loading, setLoading] = useState(true);
  const [orderQ, setOrderQ] = useState("");
  const [attQ, setAttQ] = useState("");
  const [error, setError] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [eventDoc, setEventDoc] = useState(null);
  const [showManualIssue, setShowManualIssue] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, o, a] = await Promise.all([
        reportsApi.summary(id),
        reportsApi.orders(id),
        reportsApi.attendees(id),
      ]);
      setSummary(s);
      setOrders(o);
      setAttendees(a);
      try {
        const ev = await api.get(`/events/${id}`);
        setEventDoc(ev.data);
      } catch { /* event doc used for linked-show hint only, non-critical */ }
      try {
        const f = await api.get(`/admin/events/${id}/feedback`);
        setFeedback(f.data);
      } catch { /* feedback is non-critical */ }
      try {
        const w = await api.get(`/admin/events/${id}/waitlist`);
        setWaitlist(w.data);
      } catch { /* waitlist is non-critical */ }
      try {
        const n = await api.get(`/admin/events/${id}/waitlist-nudge`);
        setNudge(n.data.nudge);
      } catch { /* nudge is advisory, non-critical */ }
      setError("");
    } catch (e) {
      setError(extractError(e));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const filteredOrders = useMemo(() => {
    if (!orderQ) return orders;
    const q = orderQ.toLowerCase();
    return orders.filter((o) =>
      (o.buyer_name || "").toLowerCase().includes(q)
      || (o.buyer_email || "").toLowerCase().includes(q)
      || (o.id || "").toLowerCase().includes(q)
    );
  }, [orders, orderQ]);

  const filteredAttendees = useMemo(() => {
    if (!attQ) return attendees;
    const q = attQ.toLowerCase();
    return attendees.filter((t) =>
      (t.buyer_name || "").toLowerCase().includes(q)
      || (t.buyer_email || "").toLowerCase().includes(q)
      || (t.buyer_phone || "").toLowerCase().includes(q)
      || (t.code || "").toLowerCase().includes(q)
      || (t.ticket_type_name || "").toLowerCase().includes(q)
    );
  }, [attendees, attQ]);

  if (loading && !summary) {
    return (
      <div className="p-8 flex items-center gap-3 text-[var(--gt-ink-2)]" data-testid="reports-loading">
        <Loader2 size={16} className="animate-spin" /><span className="text-sm">Loading reports…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8" data-testid="reports-error">
        <div className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]">{error}</div>
      </div>
    );
  }

  const t = summary?.totals || {};
  const ticketsSold = summary?.tickets_by_status
    ? (summary.tickets_by_status.valid || 0) + (summary.tickets_by_status.checked_in || 0)
    : 0;
  const checkedIn = summary?.tickets_by_status?.checked_in || 0;
  const pct = ticketsSold ? Math.round((checkedIn / ticketsSold) * 100) : 0;

  return (
    <section className="p-6 sm:p-10" data-testid="reports-page">
      <div className="max-w-6xl mx-auto">
        <Link to={`/dashboard/events/${id}/edit`} className="gt-btn-ghost -ml-4 mb-2 text-sm" data-testid="reports-back">
          <ArrowLeft size={14} /> Back to event
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="gt-label mb-2">Sales & attendance</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-tight" data-testid="reports-event-title">
              {summary?.event?.title}
            </h2>
            <div className="text-xs text-[var(--gt-ink-3)] mt-2 uppercase tracking-widest" data-testid="reports-fee-mode">
              {isAdmin && (
                <>Fee mode: {summary?.event?.fee_mode === "passed_on" ? "Passed on to buyer" : "Absorbed"}{" · "}</>
              )}
              Generated {new Date().toLocaleTimeString("en-AU")}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => load(true)} className="gt-btn-ghost text-sm" data-testid="reports-refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
            <a
              href={ordersCsvUrl(id)}
              className="gt-btn-primary text-sm py-2"
              data-testid="download-orders-csv"
            >
              <Download size={14} /> Orders CSV
            </a>
            <a
              href={attendeesCsvUrl(id)}
              className="gt-btn-secondary text-sm py-2"
              data-testid="download-attendees-csv"
            >
              <Download size={14} /> Attendees CSV
            </a>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowManualIssue(true)}
                className="gt-btn-primary text-sm py-2"
                data-testid="reports-issue-ticket-btn"
              >
                <TicketPlus size={14} /> Add Attendee / Issue Ticket
              </button>
            )}
          </div>
        </div>
        {showManualIssue && (
          <ManualIssueDialog
            open
            onClose={() => setShowManualIssue(false)}
            presetOrganiserId={eventDoc?.organiser_id || ""}
            presetEventId={id}
            onIssued={() => load(true)}
          />
        )}

        {/* Stat cards */}
        <div className={`grid grid-cols-2 gap-4 mt-8 ${isAdmin ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          <StatCard
            icon={TicketCheck}
            label="Tickets sold"
            value={ticketsSold}
            sub={`${checkedIn} checked in (${pct}%)`}
            testid="stat-tickets-sold"
          />
          <StatCard
            icon={DollarSign}
            label="Ticket sales"
            value={centsToAUD(t.gross_cents || 0)}
            sub={`${summary?.paid?.orders_count || 0} paid + ${summary?.free?.orders_count || 0} free orders`}
            testid="stat-gross"
          />
          {isAdmin && (
            <StatCard
              icon={Percent}
              label="Service fee GST"
              value={centsToAUD(t.gst_cents || 0)}
              sub={`Fees: ${centsToAUD(t.fees_cents || 0)}`}
              testid="stat-gst"
            />
          )}
          <StatCard
            icon={PiggyBank}
            label="Settlement estimate"
            value={centsToAUD(t.net_to_organiser_cents || 0)}
            sub={t.refunded_cents ? `${centsToAUD(t.refunded_cents)} refunded` : "No refunds"}
            testid="stat-net"
          />
        </div>

        {/* Sprint AA — high waitlist demand nudge (advisory only) */}
        {nudge && (
          <div className="mt-4">
            <WaitlistNudgeBanner
              nudge={nudge}
              eventId={id}
              variant="full"
              onDismissed={() => setNudge(null)}
              onViewWaitlist={() => setTab("waitlist")}
            />
          </div>
        )}

        {/* Sprint AF — Demand summary (absorbs the former Waitlist→Sales mirror card) */}
        {(waitlist.conversion?.signups || 0) + (waitlist.crosssell?.clicks_out || 0) > 0 && (
          <div className="mt-4">
            <DemandFunnelCard
              conversion={waitlist.conversion}
              crosssell={waitlist.crosssell}
              hasLinkedShows={!!eventDoc?.show_group_id}
              variant="overview"
            />
          </div>
        )}

        {/* Sprint T2 — Top Extras highlight (small, reporting-data-driven) */}
        {(summary?.addons || []).some((a) => a.sold_qty > 0) && (
          <div className="gt-card mt-8" data-testid="top-extras">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-black tracking-tight">Top extras</h3>
              <span className="gt-label">
                Extras {centsToAUD(t.addons_gross_cents || 0)}
                {t.donations_cents > 0 && ` · Donations ${centsToAUD(t.donations_cents)}`}
                {` · GST on taxable extras ${centsToAUD(t.addons_gst_cents || 0)}`}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(summary.addons || []).filter((a) => a.sold_qty > 0).slice(0, 3).map((a) => (
                <div key={a.addon_id} className="border border-[var(--gt-line)] rounded-sm p-3" data-testid={`top-extra-${a.addon_id}`}>
                  <div className="text-sm font-semibold truncate">{a.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">{a.category}{a.gst_treatment === "gst_free_genuine_gift" ? " · GST-free gift" : ""}</div>
                  <div className="mt-1 font-mono text-sm font-bold">
                    {a.sold_qty} sold · {centsToAUD(a.gross_cents)}
                    {a.refunded_cents > 0 && <span className="text-[var(--gt-accent)] text-xs"> (−{centsToAUD(a.refunded_cents)})</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sales by ticket type */}
        <div className="gt-card mt-8" data-testid="ticket-type-breakdown">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-black tracking-tight">Sales by ticket type</h3>
            <span className="gt-label">Live</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Price</th>
                  <th className="py-2 pr-3">Sold</th>
                  <th className="py-2 pr-3">Checked in</th>
                  <th className="py-2 pr-3 text-right">Ticket sales</th>
                  {isAdmin && <th className="py-2 pr-3 text-right">Service fee</th>}
                  {isAdmin && <th className="py-2 pr-3 text-right">Service fee GST</th>}
                  <th className="py-2 pl-3 text-right">Settlement est.</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.ticket_types || []).map((tt) => (
                  <tr key={tt.id} className="border-b border-[var(--gt-line)]" data-testid={`ttype-row-${tt.id}`}>
                    <td className="py-3 pr-3 font-semibold">{tt.name}</td>
                    <td className="py-3 pr-3">{tt.price_cents === 0 ? "Free" : centsToAUD(tt.price_cents)}</td>
                    <td className="py-3 pr-3">
                      <span className="font-mono">{tt.sold}</span>
                      <span className="text-[var(--gt-ink-3)]"> / {tt.quantity}</span>
                    </td>
                    <td className="py-3 pr-3 font-mono" data-testid={`ttype-checkedin-${tt.id}`}>{tt.checked_in}</td>
                    <td className="py-3 pr-3 text-right font-mono">{centsToAUD(tt.gross_cents)}</td>
                    {isAdmin && <td className="py-3 pr-3 text-right font-mono">{centsToAUD(tt.fees_cents)}</td>}
                    {isAdmin && <td className="py-3 pr-3 text-right font-mono">{centsToAUD(tt.gst_cents)}</td>}
                    <td className="py-3 pl-3 text-right font-mono font-bold">{centsToAUD(tt.net_cents)}</td>
                  </tr>
                ))}
                {(summary?.ticket_types || []).length === 0 && (
                  <tr><td colSpan={isAdmin ? 8 : 6} className="py-8 text-center text-sm text-[var(--gt-ink-3)]">No ticket types configured.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-2 border-b-2 border-[var(--gt-ink)]">
          <TabButton active={tab === "orders"} onClick={() => setTab("orders")} label="Orders" icon={Receipt} count={orders.length} testid="reports-tab-orders" />
          <TabButton active={tab === "attendees"} onClick={() => setTab("attendees")} label="Attendees" icon={Users} count={attendees.length} testid="reports-tab-attendees" />
          <TabButton active={tab === "feedback"} onClick={() => setTab("feedback")} label="Feedback" icon={Star} count={feedback.count} testid="reports-tab-feedback" />
          <TabButton active={tab === "waitlist"} onClick={() => setTab("waitlist")} label="Waitlist" icon={BellRing} count={waitlist.active} testid="reports-tab-waitlist" />
        </div>

        {tab === "waitlist" && (
          <div className="gt-card mt-4" data-testid="waitlist-panel-reports">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="font-heading text-lg font-black tracking-tight">Waitlist</h3>
              <div className="flex gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${id}/waitlist/export`}
                  className="text-xs font-semibold px-3 py-2 border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] inline-flex items-center gap-1.5"
                  data-testid="waitlist-export-csv-btn"
                >
                  <Download size={13} /> Export CSV
                </a>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const preview = await api.post(`/admin/events/${id}/waitlist/notify?dry_run=true`);
                      const would = (preview.data.preview || []).filter((p) => p.would_notify).length;
                      if (would === 0) {
                        toast.info("No waitlisted buyers can be notified right now (no matching availability).");
                        return;
                      }
                      if (!window.confirm(`This will email ${would} waitlisted buyer${would === 1 ? "" : "s"} now. Continue?`)) return;
                      const r = await api.post(`/admin/events/${id}/waitlist/notify`);
                      toast.success(`Notified ${r.data.notified} buyer${r.data.notified === 1 ? "" : "s"} (${r.data.waiting} still waiting)`);
                      load(true);
                    } catch (e) {
                      toast.error(extractError(e));
                    }
                  }}
                  className="text-xs font-bold px-3 py-2 bg-[var(--gt-ink)] text-white rounded-sm hover:opacity-90 inline-flex items-center gap-1.5"
                  data-testid="waitlist-notify-now-btn"
                >
                  <BellRing size={13} /> Notify waitlist now
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                ["Active", waitlist.active],
                ["Notified", waitlist.by_status?.notified || 0],
                ["Converted", waitlist.by_status?.converted || 0],
                ["Total", waitlist.total],
              ].map(([label, n]) => (
                <div key={label} className="border border-[var(--gt-line)] rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">{label}</div>
                  <div className="font-heading text-2xl font-black">{n}</div>
                </div>
              ))}
            </div>
            {/* Sprint AF — Demand summary (full) · Sprint AG — high-demand CTA */}
            <DemandFunnelCard
              conversion={waitlist.conversion}
              crosssell={waitlist.crosssell}
              hasLinkedShows={!!eventDoc?.show_group_id}
              variant="full"
              eventId={id}
              nudgeActive={!!nudge}
            />
            {/* Sprint Z — Waitlist → Sales conversion card */}
            {waitlist.total > 0 && waitlist.conversion && (
              <div className="border-2 border-[var(--gt-ink)] rounded-sm p-4 mb-4" data-testid="waitlist-sales-card">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={15} />
                  <span className="font-heading text-sm font-black tracking-tight">Waitlist → Sales</span>
                  <span className="gt-label ml-auto">All-time</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Conversion rate</div>
                    <div className="font-heading text-2xl font-black" data-testid="wl-conversion-rate">{waitlist.conversion.conversion_rate}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Converted</div>
                    <div className="font-heading text-2xl font-black" data-testid="wl-conversion-converted">{waitlist.conversion.converted}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Notified</div>
                    <div className="font-heading text-2xl font-black" data-testid="wl-conversion-notified">{waitlist.conversion.notified}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Converted net revenue</div>
                    <div className="font-heading text-2xl font-black" data-testid="wl-conversion-revenue">{centsToAUD(waitlist.conversion.converted_order_net_revenue_cents || 0)}</div>
                  </div>
                </div>
                {(waitlist.conversion.sections || []).some((s) => s.section !== "(event)") && (
                  <div className="mt-3 pt-3 border-t border-[var(--gt-line)]" data-testid="wl-conversion-sections">
                    <div className="gt-label mb-2">By section</div>
                    <div className="flex flex-wrap gap-2">
                      {waitlist.conversion.sections.map((s) => (
                        <span key={s.section} className="text-xs border border-[var(--gt-line)] rounded-sm px-2 py-1">
                          {s.section}: <strong>{s.converted}/{s.notified}</strong> ({s.conversion_rate}%) · {s.signups} signups
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Sprint AD — cross-sell click-through stats */}
            {waitlist.crosssell && (waitlist.crosssell.clicks_out > 0 || waitlist.crosssell.clicks_in > 0) && (
              <div className="border border-[var(--gt-line)] rounded-sm p-4 mb-4" data-testid="crosssell-stats-card">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowRight size={14} />
                  <span className="font-heading text-sm font-black tracking-tight">Cross-sell clicks</span>
                  <span className="gt-label ml-auto">Estimated from consenting sessions only</span>
                </div>
                <div className="text-sm" data-testid="crosssell-clicks-out">
                  <strong>{waitlist.crosssell.clicks_out}</strong> buyer{waitlist.crosssell.clicks_out === 1 ? "" : "s"} clicked through to your linked show from this sold-out page
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--gt-ink-3)]">
                  <span data-testid="crosssell-by-placement">
                    Sold-out panel: {waitlist.crosssell.by_placement?.event_panel || 0} · Section panel: {waitlist.crosssell.by_placement?.section_panel || 0} · Date chips: {waitlist.crosssell.by_placement?.also_playing || 0} · Waitlist email: {waitlist.crosssell.by_placement?.waitlist_email || 0}
                  </span>
                  <span data-testid="crosssell-clicks-in">{waitlist.crosssell.clicks_in} arrived here from a linked show</span>
                </div>
                {waitlist.crosssell.attributed_orders > 0 && (
                  <div className="mt-2 text-xs font-semibold" data-testid="crosssell-attributed">
                    {waitlist.crosssell.attributed_orders} attributed purchase{waitlist.crosssell.attributed_orders === 1 ? "" : "s"} · {centsToAUD(waitlist.crosssell.attributed_net_revenue_cents || 0)} attributed net revenue
                  </div>
                )}
              </div>
            )}
            {Object.keys(waitlist.active_by_section || {}).length > 0 && (
              <div data-testid="waitlist-by-section">
                <div className="gt-label mb-2">Active by section</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(waitlist.active_by_section).map(([sec, n]) => (
                    <span key={sec} className="text-xs border border-[var(--gt-line)] rounded-sm px-2 py-1">
                      {sec}: <strong>{n}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {waitlist.total === 0 && (
              <p className="text-sm text-[var(--gt-ink-2)]" data-testid="waitlist-empty">
                No waitlist entries yet. Buyers can join when the event or a section sells out.
              </p>
            )}
          </div>
        )}

        {tab === "feedback" && (
          <div className="gt-card mt-4" data-testid="feedback-panel">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="font-heading text-lg font-black tracking-tight">Attendee feedback</h3>
              {feedback.average != null && (
                <div className="flex items-center gap-2" data-testid="feedback-average">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        size={18}
                        className={n <= Math.round(feedback.average) ? "text-[var(--gt-accent)] fill-[var(--gt-accent)]" : "text-[var(--gt-ink-3)]"}
                      />
                    ))}
                  </div>
                  <span className="font-mono font-bold text-sm">{feedback.average}</span>
                  <span className="text-xs text-[var(--gt-ink-3)]">({feedback.count} rating{feedback.count === 1 ? "" : "s"})</span>
                </div>
              )}
            </div>
            {feedback.count === 0 ? (
              <p className="text-sm text-[var(--gt-ink-2)]" data-testid="feedback-empty">
                No feedback yet. Attendees receive a rating link in their post-event thank-you email (~12h after the event ends).
              </p>
            ) : (
              <div className="space-y-3" data-testid="feedback-list">
                {feedback.items.map((f, i) => (
                  <div key={i} className="border border-[var(--gt-line)] rounded-sm p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} size={13} className={n <= f.rating ? "text-[var(--gt-accent)] fill-[var(--gt-accent)]" : "text-[var(--gt-ink-3)]"} />
                        ))}
                      </div>
                      <span className="text-[10px] text-[var(--gt-ink-3)] font-mono">{(f.created_at || "").slice(0, 10)}</span>
                    </div>
                    {f.comment && <p className="mt-2 text-sm text-[var(--gt-ink-2)] whitespace-pre-line">{f.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="gt-card mt-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="font-heading text-lg font-black tracking-tight">All orders</h3>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
                <input
                  className="gt-input pl-7 py-1.5 text-xs w-64"
                  placeholder="Search buyer / email / order id"
                  value={orderQ}
                  onChange={(e) => setOrderQ(e.target.value)}
                  data-testid="orders-search"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
                    <th className="py-2 pr-3">Order</th>
                    <th className="py-2 pr-3">Buyer</th>
                    <th className="py-2 pr-3">Tickets</th>
                    <th className="py-2 pr-3 text-right">Customer total</th>
                    {isAdmin && <th className="py-2 pr-3 text-right">Service fee</th>}
                    {isAdmin && <th className="py-2 pr-3 text-right">Service fee GST</th>}
                    <th className="py-2 pr-3 text-right">Settlement est.</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pl-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody data-testid="orders-table">
                  {filteredOrders.map((o) => {
                    const remaining = Math.max(0, (o.amount_total_cents || 0) - (o.refunded_cents || 0));
                    const canAct = o.status !== "refunded" && o.status !== "cancelled";
                    return (
                      <tr key={o.id} className="border-b border-[var(--gt-line)]" data-testid={`order-row-${o.id}`}>
                        <td className="py-3 pr-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                        <td className="py-3 pr-3">
                          <div className="font-semibold">{o.buyer_name}</div>
                          <div className="text-xs text-[var(--gt-ink-3)]">{o.buyer_email}</div>
                          {o.buyer_phone && (
                            <div className="text-xs text-[var(--gt-ink-3)]" data-testid={`reports-order-phone-${o.id}`}>{o.buyer_phone}</div>
                          )}
                        </td>
                        <td className="py-3 pr-3 font-mono">{o.tickets_count}</td>
                        <td className="py-3 pr-3 text-right font-mono">{centsToAUD(o.amount_total_cents)}</td>
                        {isAdmin && <td className="py-3 pr-3 text-right font-mono">{centsToAUD(o.fee_cents)}</td>}
                        {isAdmin && <td className="py-3 pr-3 text-right font-mono">{centsToAUD(o.gst_cents)}</td>}
                        <td className="py-3 pr-3 text-right font-mono font-bold">{centsToAUD(o.net_to_organiser_cents)}</td>
                        <td className="py-3 pr-3"><Badge status={o.status} testid={`order-status-${o.id}`} /></td>
                        <td className="py-3 pr-3 text-xs text-[var(--gt-ink-2)]">{formatAuDate(o.paid_at || o.created_at)}</td>
                        <td className="py-3 pl-3 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            <Link
                              to={`/dashboard/orders/${o.id}`}
                              className="gt-btn-ghost text-xs"
                              data-testid={`view-order-btn-${o.id}`}
                              title="Open staff order view"
                            >
                              View
                            </Link>
                            {canAct ? (
                              <button
                                type="button"
                                onClick={() => setRefundTarget(o)}
                                className="gt-btn-ghost text-xs"
                                data-testid={`refund-order-btn-${o.id}`}
                                title={remaining ? `Refund up to ${centsToAUD(remaining)}` : "Void tickets"}
                              >
                                <RotateCcw size={12} /> {remaining ? "Refund" : "Void"}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredOrders.length === 0 && (
                    <tr><td colSpan={isAdmin ? 10 : 8} className="py-10 text-center text-sm text-[var(--gt-ink-3)]" data-testid="orders-empty">No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "attendees" && (
          <div className="gt-card mt-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="font-heading text-lg font-black tracking-tight">Door list</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={attendeesXlsxUrl(id)}
                  className="gt-btn-primary text-xs py-1.5"
                  data-testid="attendees-export-xlsx-btn"
                  title="Download attendees list as Excel (.xlsx)"
                >
                  <Download size={13} /> Export Excel
                </a>
                <a
                  href={attendeesCsvUrl(id)}
                  className="gt-btn-secondary text-xs py-1.5"
                  data-testid="attendees-export-csv-btn"
                  title="Download attendees list as CSV"
                >
                  <Download size={13} /> Export CSV
                </a>
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
                  <input
                    className="gt-input pl-7 py-1.5 text-xs w-64"
                    placeholder="Search name / email / phone / code / type"
                    value={attQ}
                    onChange={(e) => setAttQ(e.target.value)}
                    data-testid="attendees-search"
                  />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Attendee</th>
                    <th className="py-2 pr-3">Guest</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pl-3">Checked in</th>
                  </tr>
                </thead>
                <tbody data-testid="attendees-table">
                  {filteredAttendees.map((t) => (
                    <tr key={t.code} className="border-b border-[var(--gt-line)]" data-testid={`attendee-row-${t.code}`}>
                      <td className="py-3 pr-3 font-mono text-xs">{t.code}</td>
                      <td className="py-3 pr-3">{t.ticket_type_name}</td>
                      <td className="py-3 pr-3">
                        <div className="font-semibold">{t.buyer_name}</div>
                        <div className="text-xs text-[var(--gt-ink-3)]">{t.buyer_email}</div>
                      </td>
                      <td className="py-3 pr-3" data-testid={`reports-attendee-guest-${t.code}`}>
                        {t.attendee_name ? (
                          <>
                            <div>{t.attendee_name}</div>
                            <div className="text-xs text-[var(--gt-ink-3)]">{t.attendee_email || t.attendee_phone || ""}</div>
                          </>
                        ) : (
                          <span className="text-[var(--gt-ink-3)]">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-xs" data-testid={`reports-attendee-phone-${t.code}`}>
                        {t.buyer_phone ? (
                          <a
                            href={`tel:${String(t.buyer_phone).replace(/\s/g, "")}`}
                            className="text-[var(--gt-ink-2)] hover:text-[var(--gt-accent)]"
                          >
                            {t.buyer_phone}
                          </a>
                        ) : (
                          <span className="text-[var(--gt-ink-3)]">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <Badge status={t.status} testid={`attendee-status-${t.code}`} />
                      </td>
                      <td className="py-3 pl-3 text-xs text-[var(--gt-ink-2)]">
                        {t.checked_in_at ? (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 size={12} className="text-emerald-600" />
                            {formatAuDate(t.checked_in_at)}
                          </span>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                  {filteredAttendees.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-sm text-[var(--gt-ink-3)]" data-testid="attendees-empty">No tickets issued yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {refundTarget && (
        <RefundModal
          order={refundTarget}
          onClose={() => setRefundTarget(null)}
          onRefunded={() => load(true)}
        />
      )}
    </section>
  );
}

function TabButton({ active, onClick, label, icon: Icon, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b-2 -mb-0.5 ${
        active ? "border-[var(--gt-accent)] text-[var(--gt-ink)]" : "border-transparent text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"
      }`}
      data-testid={testid}
    >
      <Icon size={14} /> {label}
      <span className="text-[10px] font-mono text-[var(--gt-ink-3)]">({count})</span>
    </button>
  );
}
